import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import * as api from '../../lib/api';
import Pagination from '../../components/Pagination';
import { Segmented, Tag, TableCard, Th, Td, trClass, TableMessage, UserCell, fmtDateTime, fmtMoney, StickyPager } from './ui';
import { friendlyError } from '../../lib/errorReporting';

const LIMIT = 25;

const STATUS_FILTERS = [
  { key: '', label: 'Todos' },
  { key: 'pending', label: 'Pendientes' },
  { key: 'paid', label: 'Pagados' },
];

const STATUS_TAG = {
  paid: { tone: 'green', label: 'Pagado' },
  pending: { tone: 'amber', label: 'Pendiente' },
};

export default function AdminPayments() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [sendingWelcome, setSendingWelcome] = useState(null);

  const load = useCallback(async (p, s) => {
    setLoading(true);
    try {
      const res = await api.getAdminPayments({ page: p, limit: LIMIT, status: s || undefined });
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(page, status); }, [page, status, load]);

  const handleStatusChange = (s) => {
    setStatus(s);
    setPage(1);
  };

  const handleConfirm = async (reference) => {
    if (!window.confirm(
      `¿Confirmar el pago ${reference}?\n\nVerifica primero en el panel de Wompi que esta transacción esté realmente aprobada — esto le da Pro al usuario de inmediato, igual que un webhook real.`
    )) return;
    setConfirming(reference);
    try {
      await api.confirmAdminPayment(reference);
      toast.success('Pago confirmado, plan Pro extendido 30 días');
      load(page, status);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo confirmar el pago'));
    } finally {
      setConfirming(null);
    }
  };

  const handleSendWelcome = async (p) => {
    if (!window.confirm(`¿Enviar el correo de bienvenida Pro a ${p.user_email}?`)) return;
    setSendingWelcome(p.reference);
    try {
      const r = await api.sendAdminProWelcome(p.reference);
      if (r.already_sent) toast.message('Ese correo ya se había enviado');
      else if (r.sent) toast.success('Correo de bienvenida enviado');
      else toast.error('No se pudo enviar — revisa Logs → Correos');
      load(page, status);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo enviar el correo'));
    } finally {
      setSendingWelcome(null);
    }
  };

  return (
    <div className="space-y-4 pb-2" data-testid="admin-payments">
      <div className="flex items-center justify-between gap-3">
        <Segmented options={STATUS_FILTERS} value={status} onChange={handleStatusChange} />
        <p className="text-xs text-slate-500 shrink-0">{total} {total === 1 ? 'pago' : 'pagos'}</p>
      </div>

      <TableCard>
        <table className="w-full text-[13px]">
          <thead>
            <tr>
              <Th>Usuario</Th>
              <Th className="text-right">Monto</Th>
              <Th>Estado</Th>
              <Th>Real / prueba</Th>
              <Th>Último webhook</Th>
              <Th>Creado</Th>
              <Th>Pagado</Th>
              <Th>Bienvenida</Th>
              <Th>Referencia</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <TableMessage colSpan={10}>Cargando…</TableMessage>
            ) : items.length === 0 ? (
              <TableMessage colSpan={10}>Sin resultados.</TableMessage>
            ) : items.map((p) => {
              const st = STATUS_TAG[p.status] || { tone: 'muted', label: p.status };
              return (
                <tr key={p.reference} className={trClass}>
                  <Td className="max-w-[16rem]"><UserCell email={p.user_email} name={p.user_name} userId={p.user_id} /></Td>
                  <Td className="text-right tabular-nums text-slate-100 font-medium">{fmtMoney(p.amount)}</Td>
                  <Td><Tag tone={st.tone}>{st.label}</Tag></Td>
                  <Td>
                    {p.wompi_is_productive === undefined ? <span className="text-xs text-slate-500">Sin webhook aún</span>
                      : p.wompi_is_productive ? <Tag tone="sky">Real</Tag> : <Tag tone="muted">Prueba</Tag>}
                  </Td>
                  <Td className="text-xs text-slate-400">{p.last_webhook_status_raw || '—'}</Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(p.created_at)}</Td>
                  <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(p.paid_at)}</Td>
                  <Td>
                    {p.status !== 'paid' ? <span className="text-slate-600">—</span>
                      : p.welcome_email_status === 'sent' ? <Tag tone="green" title={fmtDateTime(p.welcome_email_at)}>Enviada</Tag>
                      : (
                        <button
                          onClick={() => handleSendWelcome(p)}
                          disabled={sendingWelcome === p.reference}
                          data-testid={`send-welcome-${p.reference}`}
                          className="h-7 px-2.5 rounded-full text-xs font-medium bg-white/[0.06] text-violet-300 hover:bg-white/10 transition-colors whitespace-nowrap active:scale-[0.97] disabled:opacity-50"
                        >
                          {sendingWelcome === p.reference ? 'Enviando…' : p.welcome_email_status === 'failed' ? 'Reintentar' : 'Enviar'}
                        </button>
                      )}
                  </Td>
                  <Td className="text-[11px] text-slate-500 font-mono">{p.reference}</Td>
                  <Td className="text-right">
                    {p.status !== 'paid' && (
                      <button
                        onClick={() => handleConfirm(p.reference)}
                        disabled={confirming === p.reference}
                        className="h-8 px-3 rounded-full text-xs font-medium bg-white/[0.06] text-violet-300 hover:bg-white/10 transition-colors whitespace-nowrap active:scale-[0.97] disabled:opacity-50"
                      >
                        {confirming === p.reference ? 'Confirmando…' : 'Confirmar pago'}
                      </button>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>

      <StickyPager show={total > LIMIT}>
        <Pagination page={page} pageCount={Math.max(1, Math.ceil(total / LIMIT))} onPageChange={setPage} total={total} pageSize={LIMIT} />
      </StickyPager>

      <WompiSubscriptions onAssigned={() => load(page, status)} />
    </div>
  );
}

// Monthly Pro subscriptions read from Wompi's recurring links (Fase 8). The
// ones without a user couldn't be tied to an account by email or by checkout
// time; assigning one credits the charges Wompi already reports for it.
function WompiSubscriptions({ onAssigned }) {
  const [items, setItems] = useState([]);
  const [onlyUnmatched, setOnlyUnmatched] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async (unmatched) => {
    try {
      const res = await api.getAdminWompiSubscriptions({ unmatched, limit: 50 });
      setItems(res.items);
    } catch (_) { setItems([]); }
  }, []);

  useEffect(() => { load(onlyUnmatched); }, [onlyUnmatched, load]);

  const handleAssign = async (sub) => {
    const email = window.prompt(`Email de la cuenta MiPisto para la suscripción de ${sub.name || sub.email} (${sub.email}):`, sub.email);
    if (!email) return;
    setAssigning(sub.id);
    try {
      const r = await api.assignAdminWompiSubscription(sub.id, email);
      toast.success(r.credited ? `Asignada, ${r.credited} cobro(s) acreditados` : 'Asignada');
      load(onlyUnmatched);
      onAssigned();
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo asignar'));
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="space-y-3 pt-4" data-testid="admin-wompi-subscriptions">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-200">Suscripciones mensuales (Wompi)</h3>
        <Segmented
          options={[{ key: 'all', label: 'Todas' }, { key: 'unmatched', label: 'Sin usuario' }]}
          value={onlyUnmatched ? 'unmatched' : 'all'}
          onChange={(k) => setOnlyUnmatched(k === 'unmatched')}
        />
      </div>
      <TableCard>
        <table className="w-full text-[13px]">
          <thead>
            <tr>
              <Th>Usuario</Th>
              <Th>Email en Wompi</Th>
              <Th className="text-right">Monto</Th>
              <Th>Día</Th>
              <Th>Estado</Th>
              <Th className="text-right">Cobros</Th>
              <Th>Emparejada por</Th>
              <Th>Vista</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <TableMessage colSpan={9}>Sin suscripciones.</TableMessage>
            ) : items.map((s) => (
              <tr key={s.id} className={trClass}>
                <Td className="max-w-[16rem]">
                  {s.user_id ? <UserCell email={s.user_email} name={s.user_name} userId={s.user_id} />
                    : <Tag tone="amber" title={s.unmatched_reason}>Sin usuario</Tag>}
                </Td>
                <Td className="text-xs text-slate-400">{s.email}</Td>
                <Td className="text-right tabular-nums text-slate-100">{fmtMoney(s.monto ?? s.price)}</Td>
                <Td className="text-xs text-slate-400">{s.day}</Td>
                <Td>{s.estado === 'Activa' ? <Tag tone="green">Activa</Tag> : <Tag tone="muted">{s.estado || '—'}</Tag>}</Td>
                <Td className="text-right tabular-nums text-slate-300">{s.pagos_realizados ?? 0}</Td>
                <Td className="text-xs text-slate-400">{{ email: 'Email', link_time: 'Enlace + hora', admin: 'Admin' }[s.match_method] || '—'}</Td>
                <Td className="text-xs text-slate-400 whitespace-nowrap">{fmtDateTime(s.first_seen_at)}</Td>
                <Td className="text-right">
                  {!s.user_id && (
                    <button
                      onClick={() => handleAssign(s)}
                      disabled={assigning === s.id}
                      className="h-8 px-3 rounded-full text-xs font-medium bg-white/[0.06] text-violet-300 hover:bg-white/10 transition-colors whitespace-nowrap active:scale-[0.97] disabled:opacity-50"
                    >
                      {assigning === s.id ? 'Asignando…' : 'Asignar'}
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </div>
  );
}
